import Phaser from "phaser";
import { detectPlatform } from "@/platform/detectPlatform";
import { GameConfig } from "@/config/GameConfig";
import { createButton } from "@/ui/Button";
import { contentFlowStartY, layoutButtonRows } from "@/ui/buttonLayout";
import { hexToCss } from "@/ui/colors";
import {
  ENDLESS_HEADLINE_BY_REASON,
  ENDLESS_RULES_VERSION,
  ENDLESS_RUN_CONTEXT,
  type EndlessResultSceneData,
  type EndlessSubmissionMetrics,
} from "@/endless/types";
import { formatSurvivalTime } from "@/endless/formatSurvivalTime";
import { endlessLeaderboardService } from "@/endless/EndlessLeaderboardService";
import { attemptEndlessSubmission } from "@/endless/onboardingSubmission";
import {
  trackEndlessSubmissionAttempted,
  trackEndlessSubmissionAccepted,
  trackEndlessSubmissionRejected,
  trackEndlessSubmissionFailed,
  trackEndlessNewOnlineBest,
} from "@/analytics/gameAnalytics";
import { AuthService } from "@/online/AuthService";
import { trackWorldsDiscoveryClicked, trackPlayAgainClicked } from "@/analytics/gameAnalytics";
import type { ResultSource } from "@/analytics/events";
import { attachOrientationGate, type OrientationGateHandle } from "@/scenes/OrientationGate";

/**
 * The one dedicated Endless result screen, shared by every terminal
 * outcome (death, voluntary quit, ARENA CLEARED) - never Daily's
 * `DailyResultScene` (no network submission, no attempts, no rank) and
 * never Campaign's in-`GameScene` overlays (no stars, no target-length
 * completion, no leaderboard). `create()` renders entirely from local data
 * (`resultData`) - the WORLD RANK line below is a separate, best-effort,
 * asynchronous addition that can arrive later (or never) without blocking
 * or altering the initial render; `NEW BEST` always reflects only the
 * local personal-best comparator (`EndlessStorage`) and never depends on
 * or is overloaded by the online result. Always offers a persistent
 * EXPLORE THE WORLDS secondary CTA (see `buildActionGrid()`) - a normal
 * part of the layout on every single result, never a one-time takeover.
 *
 * PR31 follow-up fix: this screen never opens the Online Features
 * invitation modal itself, and never has since - the run just ended, and
 * the intended flow is "local result screen first, PLAY AGAIN/EXPLORE
 * THE WORLDS available immediately, no automatic online interruption".
 * An earlier revision auto-opened it here on every first-ever completed
 * Endless run (gated on `OnlineFeaturesStorage.hasAsked()`) - dismissible,
 * never a hard block on the buttons, but still an unwanted automatic
 * takeover the instant the run ended. The invitation now only ever
 * appears from an explicit online/leaderboard action - pressing VIEW
 * LEADERBOARD (`viewLeaderboard()`) navigates to `EndlessLeaderboardScene`,
 * which already shows its own copy of the same modal, but only via an
 * explicit in-scene JOIN/ENABLE action, never automatically on entry
 * (see that scene's own doc) - so this screen needs no modal/consent UI
 * of its own at all, not even a dormant one.
 *
 * Post-death UX: this screen never contains a name-entry CTA and never
 * opens the shared name-entry modal - name entry lives exclusively in
 * `EndlessLeaderboardScene` now (its own SET NAME/EDIT NAME control,
 * pressed explicitly). An opted-in player's run - named or not (PR34:
 * an unnamed run submits as ANONYMOUS and picks up the name later through
 * the live profile join) - submits automatically from here with no UI at
 * all (see `trySubmitPendingRun()`'s `"submitted"` case -
 * `attemptEndlessSubmission()` is a no-op whenever Online Features aren't
 * enabled yet, so this can never surface any prompt on its own). A player
 * who hasn't opted in yet already had this run kept on the device by
 * `GameScene.finishEndlessRun()` (`EndlessLeaderboardService.captureResult()`),
 * so it can still be submitted after a later opt-in; `viewLeaderboard()`
 * additionally carries it over (`pendingMetrics`) so an ENABLE on that
 * screen reports this exact run's result.
 */
export class EndlessResultScene extends Phaser.Scene {
  private resultData!: EndlessResultSceneData;
  private mainContainer!: Phaser.GameObjects.Container;
  /** `null` until the async own-rank lookup resolves (or never, if it fails/isn't available) - see `loadWorldRank()`. */
  private worldRank: number | null = null;
  /** Guards `loadWorldRank()`'s async resolution against updating a scene that has already shut down (e.g. the player already tapped PLAY AGAIN before the network call returned). */
  private isSceneActive = false;
  /** This run's submission payload, carried over from `GameScene` - `null` for a QA preview. See `EndlessResultSceneData.metrics`. */
  private pendingMetrics: EndlessSubmissionMetrics | null = null;
  /** Once true, this run has already been submitted (in any outcome) - `trySubmitPendingRun()` becomes a permanent no-op, so a stray extra ENABLE/name-save retry can never submit the same run twice. */
  private submissionSettled = false;
  private worldsDiscoveryResultSource: ResultSource = "endless_menu";
  /** PR31 follow-up fix: see `OrientationGate.ts`'s own doc. No `requiresExplicitResume` - this Scene has no run/state to protect, so rotating back to portrait restores interaction immediately. */
  private orientationGate!: OrientationGateHandle;

  constructor() {
    super("EndlessResultScene");
  }

  create(data: EndlessResultSceneData): void {
    this.resultData = data;
    this.worldRank = null;
    this.pendingMetrics = data.metrics ?? null;
    this.submissionSettled = false;
    this.isSceneActive = true;
    this.worldsDiscoveryResultSource = data.entrySource === "instant_play" ? "instant_play" : "endless_menu";

    this.cameras.main.setBackgroundColor(GameConfig.colors.background);
    this.mainContainer = this.add.container(0, 0);

    this.buildLayout();
    this.scale.on("resize", this.onResize, this);
    this.events.once("shutdown", () => this.teardown());
    this.events.once("destroy", () => this.teardown());
    this.orientationGate = attachOrientationGate(this);
    this.orientationGate.check();

    void this.loadWorldRank();
    // Fast path for a player who is already enabled (named or not - PR34) -
    // this is what makes "subsequent Endless runs submit normally" true,
    // fully silently, with no UI on this screen at all.
    void this.trySubmitPendingRun();
  }

  private teardown(): void {
    this.isSceneActive = false;
    this.scale.off("resize", this.onResize, this);
  }

  private onResize(): void {
    this.buildLayout();
  }

  /**
   * Attempts to submit `pendingMetrics` (this screen's just-completed run)
   * via the pure `attemptEndlessSubmission()` decision - safe to call
   * repeatedly (on `create()`, after ENABLE): a no-op once
   * `submissionSettled`, and `attemptEndlessSubmission()` is itself a
   * no-op whenever Online Features aren't enabled yet or sign-in hasn't
   * completed, so this never surfaces an error on the result screen - it
   * simply leaves the run pending for the next retry. A missing display
   * name never blocks submission (PR34) - the run submits as ANONYMOUS.
   */
  private async trySubmitPendingRun(): Promise<void> {
    if (this.submissionSettled || !this.pendingMetrics) return;
    const metrics = this.pendingMetrics;
    const action = await attemptEndlessSubmission(metrics, {
      isSubmissionAvailable: () => endlessLeaderboardService.isSubmissionAvailable(),
      ensureSignedIn: () => AuthService.ensureSignedIn(),
      submitResult: (m) => endlessLeaderboardService.submitResult(m),
    });
    if (!this.isSceneActive || this.submissionSettled) return;

    switch (action.type) {
      case "none":
        return;
      case "submitted": {
        this.submissionSettled = true;
        trackEndlessSubmissionAttempted(metrics.endReason);
        switch (action.result.outcome) {
          case "sent":
            trackEndlessSubmissionAccepted(metrics.endReason, action.result.isNewBest ?? false, action.result.rank ?? null);
            // A retry of an already-recorded winning run always resolves
            // isNewBest:false (the monotonic upsert's own guarantee - see
            // record_endless_result()'s doc), so this can never double-fire
            // for the same actual improvement.
            if (action.result.isNewBest) trackEndlessNewOnlineBest(action.result.rank ?? null);
            void this.loadWorldRank();
            break;
          case "rejected":
            trackEndlessSubmissionRejected();
            break;
          case "queued":
            trackEndlessSubmissionFailed();
            break;
          case "disabled":
            break;
        }
        return;
      }
    }
  }

  /**
   * Best-effort, asynchronous, never blocking: a fresh fetch of the
   * caller's own current Endless rank, independent of (and not gated by)
   * this specific run's own submission - eventually consistent with
   * whatever the server has recorded, matching the "current rank where
   * practical" spec. A no-op whenever Online Features aren't enabled (the
   * one-time invitation modal above is the only prompt this screen ever
   * shows - never a repeated nag) or the lookup fails/comes back with no
   * result yet.
   */
  private async loadWorldRank(): Promise<void> {
    if (!endlessLeaderboardService.isSubmissionAvailable()) return;
    // PR34: the rank on THIS run's own platform board (the one it was submitted to).
    const result = await endlessLeaderboardService.getMyRank(this.pendingMetrics?.platform ?? detectPlatform(), ENDLESS_RULES_VERSION);
    if (!this.isSceneActive) return;
    if (result.state === "ok" && result.data.hasResult && result.data.rank !== null) {
      this.worldRank = result.data.rank;
      this.buildLayout();
    }
  }

  /** Immediately starts a completely fresh Endless run - no selection screen, no confirmation. A brand new `GameScene.create()` call means a brand new `LevelGame`/`RunTracker`/`InputQueue`, so speed/RUSH debt/combo/timers/WORMHOLE/SPEED TRIAL/transient visuals are all naturally reset, the same guarantee Campaign RETRY/Daily REPLAY already rely on. Never blocked by identity, name, network, or submission state. */
  private playAgain(): void {
    trackPlayAgainClicked({ gameMode: "endless" });
    this.scene.start("GameScene", { endless: ENDLESS_RUN_CONTEXT, runSource: "replay" });
  }

  /** Never blocked by identity, name, network, or submission state. */
  private mainMenu(): void {
    this.scene.start("MenuScene");
  }

  /** Instant Play & Onboarding V1: opens the existing `WorldSelectScene` exactly as Main Menu's WORLDS button does - never auto-navigates further, never changes Campaign unlocks. */
  private exploreTheWorlds(): void {
    trackWorldsDiscoveryClicked(this.worldsDiscoveryResultSource, "persistent_result_cta");
    this.scene.start("WorldSelectScene");
  }

  /**
   * Hands this run's still-unsubmitted `pendingMetrics` (if any) over to
   * `EndlessLeaderboardScene` - `undefined` once `submissionSettled`
   * (already submitted, or no metrics at all for a QA preview), so a
   * player who already submitted or never had a pending run never carries
   * anything extra. PR34: this is no longer the only way the run
   * survives - `GameScene.finishEndlessRun()` already kept it on the device
   * (`EndlessLeaderboardService.captureResult()`) when Online Features are
   * off, so PLAY AGAIN/MAIN MENU/refresh no longer lose it; carrying it
   * over here just lets an ENABLE on the leaderboard screen report THIS
   * run's own result immediately.
   */
  private viewLeaderboard(): void {
    const pendingMetrics = this.pendingMetrics && !this.submissionSettled ? this.pendingMetrics : undefined;
    this.scene.start("EndlessLeaderboardScene", { returnScene: "MenuScene", source: "result_screen", pendingMetrics });
  }

  private buildLayout(): void {
    this.mainContainer.removeAll(true);
    const { width, height } = this.scale;
    const centerX = width / 2;
    const outcome = this.resultData.outcome;
    // A short viewport (a phone in landscape) has too little vertical room
    // for the worlds-discovery text + a 3-row button grid without clipping
    // the last row - see `buildCompactActionGrid()`, the ONLY place this
    // reflows the grid itself; `renderWorldsDiscoverySection()` additionally
    // trims its own text gaps on the same threshold.
    const isCompactHeight = height < 480;

    let y = height * 0.14;

    const headline = ENDLESS_HEADLINE_BY_REASON[outcome.reason];
    const titleColor = outcome.reason === "death" ? "#ff5f6d" : outcome.reason === "arena_cleared" ? hexToCss(GameConfig.colors.star) : "#7dffb3";
    const title = this.add
      .text(centerX, y, headline, { fontFamily: "Arial, sans-serif", fontSize: "34px", fontStyle: "bold", color: titleColor })
      .setOrigin(0.5);
    this.mainContainer.add(title);
    y += 46;

    // "NEW BEST", never "NEW BEST LENGTH" - an equal-Length run can still
    // improve the record through a higher Score (see `EndlessStorage`'s
    // comparator).
    if (outcome.isNewBest) {
      const newBest = this.add
        .text(centerX, y, "NEW BEST", { fontFamily: "Arial, sans-serif", fontSize: "18px", fontStyle: "bold", color: hexToCss(GameConfig.colors.star) })
        .setOrigin(0.5);
      this.mainContainer.add(newBest);
      y += 30;
    }

    const lengthLine = this.add
      .text(centerX, y, `LENGTH ${outcome.length}`, { fontFamily: "Arial, sans-serif", fontSize: "28px", fontStyle: "bold", color: "#f5f7fa" })
      .setOrigin(0.5);
    this.mainContainer.add(lengthLine);
    y += 36;

    const scoreLine = this.add
      .text(centerX, y, `SCORE ${outcome.score}`, { fontFamily: "Arial, sans-serif", fontSize: "18px", color: "#d7dbe6" })
      .setOrigin(0.5);
    this.mainContainer.add(scoreLine);
    y += 26;

    const timeLine = this.add
      .text(centerX, y, `TIME ${formatSurvivalTime(outcome.survivalTimeMs)}`, { fontFamily: "Arial, sans-serif", fontSize: "14px", color: "#8b93a7" })
      .setOrigin(0.5);
    this.mainContainer.add(timeLine);
    y += 26;

    // Async, best-effort, shown separately from the local NEW BEST badge -
    // absent entirely until (and unless) loadWorldRank() actually resolves
    // one. Never a replacement for, or blended with, the local comparator.
    if (this.worldRank !== null) {
      const rankLine = this.add
        .text(centerX, y, `WORLD RANK #${this.worldRank}`, {
          fontFamily: "Arial, sans-serif",
          fontSize: "13px",
          fontStyle: "bold",
          color: "#5b8cff",
        })
        .setOrigin(0.5);
      this.mainContainer.add(rankLine);
      y += 22;
    }
    y += 10;

    y = this.renderWorldsDiscoverySection(centerX, y, width, height);

    if (isCompactHeight) {
      this.buildCompactActionGrid(centerX, y, width, height);
    } else {
      this.buildActionGrid(centerX, y, width, height);
    }
  }

  /**
   * The normal (non-compact) action grid - EXPLORE THE WORLDS, PLAY
   * AGAIN/MAIN MENU, then VIEW LEADERBOARD, on every single Endless
   * result. PLAY AGAIN keeps `createButton()`'s own default (unstyled)
   * accent color - the one visually primary action on this screen; EXPLORE
   * THE WORLDS uses the same secondary dark fill as MAIN MENU/VIEW
   * LEADERBOARD, with gold text so it still reads as clearly noticeable
   * without competing with PLAY AGAIN for primary billing.
   */
  private buildActionGrid(centerX: number, y: number, width: number, height: number): void {
    const gridWidth = Math.min(380, width - 40);
    const gridRowHeight = Math.max(56, Math.min(72, height * 0.086));
    const gridStartY = contentFlowStartY(y, height, { floor: 28, cap: 44, factor: 0.045 });
    const rows = [[{}], [{}, {}], [{}]];
    const { placements } = layoutButtonRows(rows, { availableWidth: gridWidth, startY: gridStartY, rowHeight: gridRowHeight });
    const [explorePlacement] = placements[0];
    const [playAgainPlacement, menuPlacement] = placements[1];
    const [leaderboardPlacement] = placements[2];

    const exploreButton = createButton(this, centerX + explorePlacement!.x, explorePlacement!.y, "EXPLORE THE WORLDS", () => this.exploreTheWorlds(), {
      width: explorePlacement!.width,
      fontSize: explorePlacement!.isTwoColumn ? "15px" : "18px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: hexToCss(GameConfig.colors.star),
    });
    this.mainContainer.add(exploreButton);

    const playAgainButton = createButton(this, centerX + playAgainPlacement!.x, playAgainPlacement!.y, "PLAY AGAIN", () => this.playAgain(), {
      width: playAgainPlacement!.width,
      fontSize: playAgainPlacement!.isTwoColumn ? "15px" : "20px",
    });
    this.mainContainer.add(playAgainButton);

    const menuButton = createButton(this, centerX + menuPlacement!.x, menuPlacement!.y, "MAIN MENU", () => this.mainMenu(), {
      width: menuPlacement!.width,
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(menuButton);

    const leaderboardButton = createButton(this, centerX + leaderboardPlacement!.x, leaderboardPlacement!.y, "VIEW LEADERBOARD", () => this.viewLeaderboard(), {
      width: leaderboardPlacement!.width,
      fontSize: leaderboardPlacement!.isTwoColumn ? "15px" : "16px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: "#f5f7fa",
    });
    this.mainContainer.add(leaderboardButton);
  }

  /**
   * Short-viewport (landscape phone) reflow - reached only when
   * `isCompactHeight` (see `buildLayout()`). EXPLORE THE WORLDS keeps its
   * own full-width row (same secondary-dark/gold-text treatment as the
   * normal grid); PLAY AGAIN/MAIN MENU/VIEW LEADERBOARD sit in a single
   * 3-across row below it, trading the vertical room a 3rd stacked row
   * would cost for the horizontal room a landscape phone already has
   * plenty of. Every button here still keeps `createButton()`'s default
   * 54px height - a comfortably tappable target, never reduced for this
   * reflow.
   */
  private buildCompactActionGrid(centerX: number, y: number, width: number, height: number): void {
    // Landscape phones have width to spare even when height is tight -
    // wider than the portrait/desktop 380px cap so three buttons
    // (VIEW LEADERBOARD included) each get comfortable room, but still
    // capped so it never stretches uncomfortably wide on a big screen.
    const rowWidth = Math.min(560, width - 40);
    const rowHeight = Math.max(56, Math.min(64, height * 0.08));
    const gridStartY = contentFlowStartY(y, height, { floor: 16, cap: 28, factor: 0.03 });

    const exploreY = gridStartY;
    const exploreButton = createButton(this, centerX, exploreY, "EXPLORE THE WORLDS", () => this.exploreTheWorlds(), {
      width: Math.min(340, rowWidth),
      fontSize: "16px",
      fillColor: 0x1c2030,
      hoverColor: 0x262c40,
      textColor: hexToCss(GameConfig.colors.star),
    });
    this.mainContainer.add(exploreButton);

    const secondaryY = exploreY + rowHeight;
    const gap = 10;
    const buttonWidth = (rowWidth - gap * 2) / 3;
    const startX = centerX - rowWidth / 2 + buttonWidth / 2;
    const secondaryStyle = { width: buttonWidth, fontSize: "13px", fillColor: 0x1c2030, hoverColor: 0x262c40, textColor: "#f5f7fa" } as const;

    // No fillColor/hoverColor/textColor override - createButton()'s own
    // default accent color is what makes this the visually primary action,
    // same as the normal (non-compact) grid above.
    const playAgainButton = createButton(this, startX, secondaryY, "PLAY AGAIN", () => this.playAgain(), { width: buttonWidth, fontSize: "13px" });
    this.mainContainer.add(playAgainButton);

    const menuButton = createButton(this, startX + (buttonWidth + gap), secondaryY, "MAIN MENU", () => this.mainMenu(), secondaryStyle);
    this.mainContainer.add(menuButton);

    const leaderboardButton = createButton(this, startX + (buttonWidth + gap) * 2, secondaryY, "VIEW LEADERBOARD", () => this.viewLeaderboard(), secondaryStyle);
    this.mainContainer.add(leaderboardButton);
  }

  /**
   * The persistent worlds-discovery supporting copy - a normal part of
   * EVERY Endless result screen (never a one-time takeover), rendered
   * above the action grid, after the full Endless result stats (never
   * replacing them). Returns the Y just past its own content, for the
   * caller to continue laying out the button grid from.
   */
  private renderWorldsDiscoverySection(centerX: number, y: number, width: number, height: number): number {
    // A short viewport (a phone in landscape) has very little vertical
    // room to spare above the button grid - tightening this section's own
    // gaps (never its wording/colors/hierarchy) is a small, contained way
    // to give the grid a bit more room back, without touching the shared
    // result-stats block above it. The journey line is dropped entirely
    // on a compact viewport rather than tightened further - see the "core
    // requirement is the CTA + supporting copy" rule; the journey line is
    // the one purely-optional extra.
    const isCompactHeight = height < 480;
    const wrapWidth = Math.min(420, width - 40);
    const supportingLine = this.add
      .text(centerX, y, "Beat stages • Earn stars • Unlock new worlds", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        fontStyle: "bold",
        color: hexToCss(GameConfig.colors.star),
        align: "center",
        wordWrap: { width: wrapWidth },
      })
      .setOrigin(0.5);
    this.mainContainer.add(supportingLine);
    y = supportingLine.y + supportingLine.height + (isCompactHeight ? 4 : 8);

    if (isCompactHeight) return y;

    const journeyLine = this.add
      .text(centerX, y, "MOON → EARTH → MARS → SATURN → SUN", {
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        color: "#8b93a7",
        align: "center",
        wordWrap: { width: wrapWidth },
      })
      .setOrigin(0.5);
    this.mainContainer.add(journeyLine);
    return journeyLine.y + journeyLine.height + 12;
  }
}
